import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { getProductImages } from "@/lib/productImages";
import { getFixedPrice, formatFixedPrice } from "@/lib/fixedPrices";
import { getProductType } from "@/lib/productRegistry";
import { ArrowRight } from "lucide-react";

interface ProductCTAProps {
  handles: string[];
}

export function ProductCTA({ handles }: ProductCTAProps) {
  const { language, currency, t } = useLanguage();
  const displayCurrency = currency || 'USD';

  return (
    <section className="mt-12 mb-8">
      <h2 className="text-2xl font-serif font-semibold text-foreground mb-6">
        {t('blog.recommendedProducts')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {handles.map(handle => {
          const images = getProductImages(handle);
          const heroImage = images?.[0]?.src;
          const titleKey = `productContent.title.${getProductType(handle)}`;
          const translated = t(titleKey);
          const name = translated !== titleKey ? translated : handle;
          const priceData = getFixedPrice(handle, displayCurrency);
          const salePrice = priceData?.sale ?? 0;
          const originalPrice = priceData?.original ?? 0;

          return (
            <Link
              key={handle}
              to={`/product/${handle}`}
              className="group flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:shadow-md hover:border-primary/30 transition-all"
            >
              {heroImage && (
                <img
                  src={heroImage}
                  alt={name}
                  className="w-16 h-16 object-contain rounded-lg flex-shrink-0"
                  loading="lazy"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                  {name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm font-bold text-primary">
                    {formatFixedPrice(salePrice, displayCurrency)}
                  </span>
                  {originalPrice > salePrice && (
                    <span className="text-xs text-muted-foreground line-through">
                      {formatFixedPrice(originalPrice, displayCurrency)}
                    </span>
                  )}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" aria-hidden="true" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
